import { ArrowLeft, CheckCircle, Circle, Clock } from 'lucide-react';
import { useApplication } from '../hooks/queries/useApplications';
import type { ApplicationStatus } from '@applicant-reviewer/api';

const STATUS_LABELS: Record<ApplicationStatus, string> = {
  draft: "Draft",
  submitted: "Submitted",
  reviewing: "Under Review",
  interview: "Interview Scheduled",
  offered: "Offer Extended",
  hired: "Hired",
  benched: "On Bench",
  rejected: "Not Selected",
};

const STATUS_COLORS: Record<ApplicationStatus, string> = {
  draft: "bg-gray-100 text-gray-500 border border-gray-200",
  submitted: "bg-blue-50 text-blue-700 border border-blue-200",
  reviewing: "bg-blue-50 text-blue-700 border border-blue-200",
  interview: "bg-purple-50 text-purple-700 border border-purple-200",
  offered: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  hired: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  benched: "bg-amber-50 text-amber-700 border border-amber-200",
  rejected: "bg-gray-100 text-gray-500 border border-gray-200",
};

const WHAT_HAPPENS_NEXT: Record<ApplicationStatus, string> = {
  draft: "Your application is saved as a draft. Submit it when you're ready.",
  submitted: "Your application is being reviewed. We'll notify you of next steps.",
  reviewing: "Your application is being reviewed. We'll notify you of next steps.",
  interview: "You've been invited to interview! Check your email for scheduling details.",
  offered: "Congratulations! An offer has been extended.",
  hired: "Congratulations! You've been hired.",
  benched: "You've been placed on our priority list for future openings.",
  rejected: "This position has been filled. Consider browsing other open jobs.",
};

// The five canonical pipeline stages shown in the vertical stepper
type StepperStage = "applied" | "reviewing" | "interview" | "offer" | "hired";

const STEPPER_STAGES: { key: StepperStage; label: string }[] = [
  { key: "applied",   label: "Applied" },
  { key: "reviewing", label: "Under Review" },
  { key: "interview", label: "Interview" },
  { key: "offer",     label: "Offer" },
  { key: "hired",     label: "Hired" },
];

// Map an ApplicationStatus to the active stepper index (0-based)
function getActiveStepIndex(status: ApplicationStatus): number {
  switch (status) {
    case "draft":      return 0;
    case "submitted":  return 1;
    case "reviewing":  return 1;
    case "interview":  return 2;
    case "offered":    return 3;
    case "hired":      return 4;
    case "benched":    return 1; // benched stays at review stage
    case "rejected":   return 1; // rejected stays at review stage
    default:           return 0;
  }
}

interface CandidateApplicationDetailProps {
  id: string;
  onBack: () => void;
}

export default function CandidateApplicationDetail({ id, onBack }: CandidateApplicationDetailProps) {
  const { data: application, isLoading, isError } = useApplication(id, 'candidate');

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-[#0156FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (isError || !application) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-6">
        <div className="text-center">
          <p className="text-xl font-bold text-gray-900 mb-2">Application Not Found</p>
          <p className="text-base text-gray-500">This application could not be loaded.</p>
          <button
            type="button"
            onClick={onBack}
            className="mt-6 flex items-center gap-2 mx-auto text-sm text-[#0156FF] hover:underline"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to My Applications
          </button>
        </div>
      </div>
    );
  }

  const activeStep = getActiveStepIndex(application.status);
  const isTerminal = application.status === 'rejected';
  const whatNext = WHAT_HAPPENS_NEXT[application.status];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-5">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          My Applications
        </button>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-extrabold text-gray-900" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Position
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Applied {new Date(application.submittedAt ?? application.createdAt).toLocaleDateString()}
            </p>
          </div>
          <span className={`flex-shrink-0 text-xs font-semibold px-3 py-1 rounded-full ${STATUS_COLORS[application.status]}`}>
            {STATUS_LABELS[application.status]}
          </span>
        </div>
      </header>

      <div className="px-4 py-6 max-w-2xl mx-auto space-y-4">

        {/* Vertical Stepper — hidden for rejected/bench terminal states */}
        {!isTerminal && application.status !== 'benched' && (
          <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-5">
              Application Progress
            </h2>
            <ol className="relative">
              {STEPPER_STAGES.map((stage, idx) => {
                const isCompleted = idx < activeStep;
                const isCurrent   = idx === activeStep;
                const isFuture    = idx > activeStep;
                const isLast      = idx === STEPPER_STAGES.length - 1;

                return (
                  <li key={stage.key} className="flex gap-4">
                    {/* Icon + vertical connector */}
                    <div className="flex flex-col items-center">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                          isCompleted
                            ? 'bg-[#0156FF] text-white'
                            : isCurrent
                            ? 'bg-blue-50 text-[#0156FF] border-2 border-[#0156FF]'
                            : 'bg-gray-100 text-gray-400'
                        }`}
                      >
                        {isCompleted ? (
                          <CheckCircle className="w-4 h-4" />
                        ) : isCurrent ? (
                          <Clock className="w-4 h-4" />
                        ) : (
                          <Circle className="w-4 h-4" />
                        )}
                      </div>
                      {!isLast && (
                        <div
                          className={`w-0.5 flex-1 my-1 ${
                            isCompleted ? 'bg-[#0156FF]' : 'bg-gray-200'
                          }`}
                          style={{ minHeight: '1.5rem' }}
                        />
                      )}
                    </div>

                    {/* Label */}
                    <div className="pb-6 pt-1">
                      <span
                        className={`text-sm font-semibold ${
                          isCompleted
                            ? 'text-[#0156FF]'
                            : isCurrent
                            ? 'text-gray-900'
                            : isFuture
                            ? 'text-gray-400'
                            : 'text-gray-700'
                        }`}
                      >
                        {stage.label}
                      </span>
                      {isCurrent && (
                        <p className="text-xs text-gray-500 mt-0.5">Current stage</p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          </section>
        )}

        {/* What Happens Next */}
        {whatNext && (
          <section
            className={`rounded-2xl border p-5 ${
              application.status === 'rejected'
                ? 'bg-gray-50 border-gray-200'
                : application.status === 'offered' || application.status === 'hired'
                ? 'bg-emerald-50 border-emerald-100'
                : application.status === 'benched'
                ? 'bg-amber-50 border-amber-100'
                : application.status === 'interview'
                ? 'bg-purple-50 border-purple-100'
                : 'bg-blue-50 border-blue-100'
            }`}
          >
            <h2
              className={`text-sm font-bold uppercase tracking-wide mb-2 ${
                application.status === 'rejected'
                  ? 'text-gray-600'
                  : application.status === 'offered' || application.status === 'hired'
                  ? 'text-emerald-700'
                  : application.status === 'benched'
                  ? 'text-amber-700'
                  : application.status === 'interview'
                  ? 'text-purple-700'
                  : 'text-blue-700'
              }`}
            >
              What Happens Next
            </h2>
            <p className="text-sm text-gray-700">{whatNext}</p>
          </section>
        )}

        {/* Submitted Answers */}
        {application.answers && application.answers.length > 0 && (
          <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-4">
              Your Answers
            </h2>
            <div className="space-y-4">
              {application.answers.map((answer) => (
                <div key={answer.questionId}>
                  <p className="text-sm text-gray-600">
                    {answer.textAnswer ?? '(No text answer)'}
                  </p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Browse More Jobs CTA */}
        <div className="pt-2 pb-6 text-center">
          <button
            type="button"
            onClick={() => { window.location.href = '/jobs'; }}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#0156FF] text-white text-sm font-semibold hover:bg-blue-700 transition-colors shadow-sm"
          >
            Browse More Jobs
          </button>
        </div>

      </div>
    </div>
  );
}
